import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { healthApi, type HealthMetricsParams } from '@/lib/api/health'
import type { ConnectHealthRequest } from '@/types'

const QUERY_KEYS = {
  connections: (familyId: string) => ['health', 'connections', familyId] as const,
  metrics: (familyId: string, params?: HealthMetricsParams) =>
    ['health', 'metrics', familyId, params] as const,
  parentMetrics: (familyId: string, parentUserId: string, params?: Omit<HealthMetricsParams, 'parent_user_id'>) =>
    ['health', 'metrics', familyId, parentUserId, params] as const,
  summary: (familyId: string, params?: { parent_user_id?: string; period?: string }) =>
    ['health', 'summary', familyId, params] as const,
  trends: (familyId: string, params?: { parent_user_id?: string; period?: string }) =>
    ['health', 'trends', familyId, params] as const,
}

export type HealthPeriod = '7d' | '30d' | '90d'

export function useHealthConnections(familyId: string | null) {
  const queryClient = useQueryClient()

  const connectionsQuery = useQuery({
    queryKey: QUERY_KEYS.connections(familyId!),
    queryFn: () => healthApi.getConnections(familyId!),
    enabled: !!familyId,
    staleTime: 5 * 60 * 1000,
  })

  const connectMutation = useMutation({
    mutationFn: (data: ConnectHealthRequest) => healthApi.connect(familyId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.connections(familyId!) })
    },
  })

  const disconnectMutation = useMutation({
    mutationFn: (connectionId: string) => healthApi.disconnect(familyId!, connectionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.connections(familyId!) })
    },
  })

  return {
    connections: connectionsQuery.data ?? [],
    isLoading: connectionsQuery.isLoading,
    error: connectionsQuery.error,
    refetch: connectionsQuery.refetch,

    connect: connectMutation.mutateAsync,
    disconnect: disconnectMutation.mutateAsync,
    isConnecting: connectMutation.isPending,
    isDisconnecting: disconnectMutation.isPending,
  }
}

export function useHealthMetrics(
  familyId: string | null,
  params?: HealthMetricsParams
) {
  return useQuery({
    queryKey: QUERY_KEYS.metrics(familyId!, params),
    queryFn: () => healthApi.getMetrics(familyId!, params),
    enabled: !!familyId,
    staleTime: 1 * 60 * 1000, // 1 minute
  })
}

export function useMemberHealthMetrics(
  familyId: string | null,
  parentUserId: string | null,
  params?: Omit<HealthMetricsParams, 'parent_user_id'>
) {
  return useQuery({
    queryKey: QUERY_KEYS.parentMetrics(familyId!, parentUserId!, params),
    queryFn: () => healthApi.getMetricsByParent(familyId!, parentUserId!, params),
    enabled: !!familyId && !!parentUserId,
    staleTime: 1 * 60 * 1000,
  })
}

export function useHealthSummary(
  familyId: string | null,
  params?: { parent_user_id?: string; period?: HealthPeriod }
) {
  // parent_user_id is required by the backend for health summary
  return useQuery({
    queryKey: QUERY_KEYS.summary(familyId!, params),
    queryFn: () => healthApi.getSummary(familyId!, params),
    enabled: !!familyId && !!params?.parent_user_id,
    staleTime: 5 * 60 * 1000,
  })
}

export function useHealthTrends(
  familyId: string | null,
  params?: { parent_user_id?: string; period?: HealthPeriod }
) {
  // parent_user_id is required by the backend for health trends
  return useQuery({
    queryKey: QUERY_KEYS.trends(familyId!, params),
    queryFn: () => healthApi.getTrends(familyId!, params),
    enabled: !!familyId && !!params?.parent_user_id,
    staleTime: 5 * 60 * 1000,
  })
}

// Helper to generate date range for period
export function getPeriodDateRange(period: HealthPeriod): { start_date: string; end_date: string } {
  const end = new Date()
  const start = new Date()

  switch (period) {
    case '7d':
      start.setDate(end.getDate() - 7)
      break
    case '30d':
      start.setDate(end.getDate() - 30)
      break
    case '90d':
      start.setDate(end.getDate() - 90)
      break
  }

  return {
    start_date: start.toISOString().split('T')[0],
    end_date: end.toISOString().split('T')[0],
  }
}

// Helper to group metrics by date
export function groupMetricsByDate(metrics: Array<{ timestamp: string; value: number }>) {
  const grouped: Record<string, number[]> = {}

  metrics.forEach((metric) => {
    const date = metric.timestamp.split('T')[0]
    if (!grouped[date]) {
      grouped[date] = []
    }
    grouped[date].push(metric.value)
  })

  return Object.entries(grouped).map(([date, values]) => ({
    date,
    value: values.reduce((a, b) => a + b, 0) / values.length,
    min: Math.min(...values),
    max: Math.max(...values),
    count: values.length,
  }))
}
